<template>
  <div class="gatehouse-page">
    <section class="list-header">
      <div class="title-row">
        <v-btn icon class="back-button" @click="$router.push('/home')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h1>Portaria</h1>
      </div>
      <div class="header-actions">
        <v-btn depressed color="primary" class="add-button" @click="openAuthorizationSheet">
          <v-icon left small>mdi-plus</v-icon>
          Adicionar
        </v-btn>
        <v-btn icon class="refresh-button" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <section class="authorizations">
      <h2>Autorizações recentes</h2>
      <article
        v-for="item in items"
        :key="item.id"
        class="auth-card"
        :class="{
          'is-pending': item.status === 'pending_approval',
          'is-rejected': item.status === 'rejected',
        }"
      >
        <div>
          <strong>{{ item.visitor_name }}</strong>
          <span>{{ formatVisitType(item.visit_type) }} - {{ formatStatus(item.status) }}</span>
          <small v-if="item.status === 'pending_approval'" class="approval-hint">
            A portaria solicitou sua autorização para liberar a entrada.
          </small>
          <small v-if="item.photo_resource_id" class="attachment-hint">
            <v-icon size="14">mdi-image-outline</v-icon>
            Foto anexada
          </small>
        </div>
        <div v-if="item.status === 'pending_approval'" class="approval-actions">
          <v-btn
            small
            outlined
            color="error"
            :loading="isActionLoading(item, 'reject')"
            :disabled="Boolean(authorizationActionLoading)"
            @click="respondAuthorization(item, 'reject')"
          >
            Rejeitar
          </v-btn>
          <v-btn
            small
            depressed
            color="primary"
            :loading="isActionLoading(item, 'approve')"
            :disabled="Boolean(authorizationActionLoading)"
            @click="respondAuthorization(item, 'approve')"
          >
            Aprovar
          </v-btn>
        </div>
        <div v-else class="code-box">
          <small>Código</small>
          <strong v-if="item.status !== 'rejected'">{{ item.access_code }}</strong>
          <strong v-else>--</strong>
        </div>
      </article>

      <div v-if="!items.length" class="empty-panel">
        <v-icon color="primary" size="34">mdi-gate</v-icon>
        <strong>Nenhuma autorização</strong>
        <span>As liberações criadas aparecerão aqui.</span>
      </div>
    </section>

    <v-bottom-sheet v-model="authorizationSheet" inset>
      <v-card class="authorization-sheet">
        <div class="sheet-handle" />
        <div class="sheet-header">
          <div>
            <span>Portaria</span>
            <strong>Autorizar visitante</strong>
          </div>
          <v-btn icon class="close-button" @click="authorizationSheet = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <section class="form-panel">
          <v-text-field v-model="form.visitor_name" label="Nome do visitante" outlined dense hide-details="auto" />
          <v-select v-model="form.visit_type" :items="visitTypes" label="Tipo de acesso" outlined dense hide-details="auto" />
          <v-text-field
            v-model="form.vehicle_plate"
            label="Placa do veículo"
            placeholder="ABC-1234 ou ABC1D23"
            outlined
            dense
            hide-details="auto"
            maxlength="8"
            @input="handlePlateInput"
          />
          <v-file-input
            v-model="authorizationPhoto"
            label="Foto da autorização"
            prepend-icon="mdi-camera-outline"
            accept="image/*"
            outlined
            dense
            show-size
            truncate-length="24"
            hide-details="auto"
          />
          <v-textarea v-model="form.notes" label="Observação" outlined rows="2" hide-details="auto" />
          <v-btn block depressed color="primary" class="save-button" :loading="saving" @click="save">
            Gerar autorização
          </v-btn>
        </section>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaGatehouseAuthorization",
  data() {
    return {
      saving: false,
      authorizationSheet: false,
      authorizationActionLoading: null,
      items: [],
      authorizationPhoto: null,
      form: {
        visitor_name: "",
        visit_type: "visitor",
        vehicle_plate: "",
        notes: "",
      },
      visitTypes: [
        { text: "Visitante", value: "visitor" },
        { text: "Prestador", value: "service" },
        { text: "Entrega", value: "delivery" },
        { text: "Motorista", value: "driver" },
      ],
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    profile() {
      return JSON.parse(localStorage.getItem("associationProfile") || "{}");
    },
    async load() {
      const profile = this.profile();
      if (!profile.tenant_id) return;
      const response = await axios.get("/visitor-authorizations", {
        params: { tenant_id: profile.tenant_id, limit: 30 },
      });
      this.items = response.data.content.data || response.data.content || [];
    },
    async save() {
      if (!this.form.visitor_name) return;
      const profile = this.profile();
      this.saving = true;
      try {
        const photoResourceId = await this.uploadAuthorizationPhoto();
        await axios.post("/visitor-authorizations", {
          ...this.form,
          vehicle_plate: this.normalizeVehiclePlate(this.form.vehicle_plate),
          photo_resource_id: photoResourceId,
          tenant_id: profile.tenant_id,
          member_id: profile.id,
        });
        this.resetForm();
        await this.load();
        this.authorizationSheet = false;
      } finally {
        this.saving = false;
      }
    },
    async respondAuthorization(item, decision) {
      this.authorizationActionLoading = `${item.id}:${decision}`;
      try {
        await axios.post(`/visitor-authorizations/${item.id}/respond`, { decision });
        await this.load();
      } finally {
        this.authorizationActionLoading = null;
      }
    },
    isActionLoading(item, decision) {
      return this.authorizationActionLoading === `${item.id}:${decision}`;
    },
    openAuthorizationSheet() {
      this.resetForm();
      this.authorizationSheet = true;
    },
    resetForm() {
      this.form = { visitor_name: "", visit_type: "visitor", vehicle_plate: "", notes: "" };
      this.authorizationPhoto = null;
    },
    async uploadAuthorizationPhoto() {
      if (!this.authorizationPhoto) return null;

      const formData = new FormData();
      formData.append("file", this.authorizationPhoto);
      formData.append("name", this.authorizationPhoto.name || "Foto da autorização");

      const upload = await axios.post("/resources", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return upload.data.content && upload.data.content.id;
    },
    handlePlateInput(value) {
      const formatted = this.formatVehiclePlate(value);
      if (formatted !== value) {
        this.form.vehicle_plate = formatted;
      }
    },
    formatVehiclePlate(value) {
      const clean = this.normalizeVehiclePlate(value);
      if (/^[A-Z]{3}\d{4}$/.test(clean)) {
        return `${clean.slice(0, 3)}-${clean.slice(3)}`;
      }
      return clean;
    },
    normalizeVehiclePlate(value) {
      return String(value || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 7);
    },
    formatVisitType(value) {
      const type = this.visitTypes.find((item) => item.value === value);
      return type ? type.text : value || "Visitante";
    },
    formatStatus(value) {
      const labels = {
        authorized: "Autorizado",
        inside: "Dentro do local",
        finished: "Finalizado",
        canceled: "Cancelado",
        pending_approval: "Aguardando aprovação",
        rejected: "Rejeitado",
      };
      return labels[value] || value || "Autorizado";
    },
  },
};
</script>

<style scoped lang="scss">
.gatehouse-page {
  min-height: 100%;
  padding: 0 16px 18px;
  color: #111827;
}

.list-header {
  min-height: 72px;
  margin: 0 -16px 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #edf0ed;

  h1 {
    margin: 0;
    font-size: 24px;
  }
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button,
.refresh-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
  border-radius: 12px;
}

.add-button {
  min-height: 38px;
  padding: 0 14px !important;
  border-radius: 12px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.auth-card,
.empty-panel {
  background: #ffffff;
  border: 1px solid #e1e8e2;
  border-radius: 16px;
}

.form-panel {
  display: grid;
  gap: 12px;

  h2 {
    margin: 0 0 4px;
    font-size: 18px;
  }
}

.authorizations {
  margin-top: 0;

  h2 {
    margin: 0 0 10px;
    font-size: 18px;
  }
}

.authorization-sheet {
  max-height: calc(100vh - 88px);
  padding: 10px 18px 22px;
  color: #111827;
  border-radius: 24px 24px 0 0;
  overflow-y: auto;
}

.sheet-handle {
  width: 42px;
  height: 4px;
  margin: 0 auto 14px;
  background: #d3ded7;
  border-radius: 999px;
}

.sheet-header {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  span,
  strong {
    display: block;
  }

  span {
    color: #267062;
    font-size: 12px;
    font-weight: 850;
    text-transform: uppercase;
  }

  strong {
    margin-top: 2px;
    color: #111827;
    font-size: 20px;
    line-height: 1.15;
  }
}

.close-button {
  background: #f5f7f5;
  border: 1px solid #dfe7df;
}

.save-button {
  height: 48px !important;
  border-radius: 14px;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: none;
}

.auth-card {
  min-height: 82px;
  margin-bottom: 10px;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  strong,
  span,
  small {
    display: block;
  }

  span,
  small {
    color: #66736b;
    font-size: 12px;
  }
}

.auth-card.is-pending {
  align-items: flex-start;
  border-color: #bfe0cf;
  background: #f7fbf8;
}

.auth-card.is-rejected {
  opacity: 0.78;
}

.approval-hint {
  max-width: 360px;
  margin-top: 5px;
  line-height: 1.35;
}

.approval-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .v-btn {
    min-width: 82px !important;
    height: 32px !important;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 850;
    letter-spacing: 0;
    text-transform: none;
  }
}

.attachment-hint {
  margin-top: 4px;
  display: inline-flex !important;
  align-items: center;
  gap: 4px;
  color: #057a3d !important;
}

.code-box {
  min-width: 92px;
  padding: 9px;
  background: #eef7f1;
  border-radius: 12px;
  text-align: center;

  strong {
    color: #057a3d;
    font-size: 20px;
  }
}

.empty-panel {
  min-height: 220px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  text-align: center;

  span {
    color: #66736b;
    font-size: 13px;
  }
}

@media (max-width: 520px) {
  .list-header {
    align-items: flex-start;
  }

  .header-actions {
    gap: 6px;
  }

  .add-button {
    min-width: 0 !important;
    padding: 0 11px !important;
  }

  .auth-card {
    align-items: stretch;
    flex-direction: column;
  }

  .approval-actions {
    justify-content: flex-end;
  }

  .code-box {
    align-self: flex-end;
  }
}
</style>
